// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a dialog component for creating new locations.
 * It handles both basic locations and locations with full address details,
 * integrating with Google Places for address validation.
 */
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { hasUniqueViolationError } from '@/api/error-utils';
import { CompanySearch } from '@/components/company-search';
import { FormAddressSearch } from '@/components/form-address-search';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  ArticleOutlined,
  TuneOutlined,
} from '@/constants/hardfin-standard-icons';
import { getNewCompanyURL } from '@/customer/utilities/get-new-customer-url';
import { createLocation } from '@/location/api/create-location';
import { LocationInventoryToggle } from '@/location/components/location-inventory-toggle';
import {
  locationComponentsSchema,
  sparseCustomerSchema,
} from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating company data.
 * Requires basic company information and status flags.
 */
const companySchema = z.object(sparseCustomerSchema.shape, {
  message: 'Company is required',
});

/**
 * Schema for validating location names.
 * Ensures non-empty string values.
 */
const nameSchema = z
  .string({ required_error: 'Name is required' })
  .min(1, 'Name is required');

/**
 * Schema for validating location addresses.
 * Supports both full addresses and locations without addresses.
 * When an address is provided, ensures all required fields are present.
 */
const addressSchema = z
  .object({
    placeId: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    address: locationComponentsSchema,
  })
  .nullable()
  .refine(
    (value) => value === null || (value.address.formattedAddress ?? '') !== '',
    { message: 'Address is required' }
  );

interface InputFormData {
  company: z.infer<typeof companySchema> | null;
  name: string;
  address: z.infer<typeof addressSchema>;
  isInventory: boolean | null;
}

const outputSchema = z.object({
  company: companySchema,
  name: nameSchema,
  address: addressSchema,
  isInventory: z.boolean(),
});

type OutputFormData = z.infer<typeof outputSchema>;

interface Props {
  /**
   * Optional default name for the new location.
   * Used when pre-filling the name field.
   */
  defaultName?: string;

  /**
   * Whether the dialog is currently visible.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog should close.
   * Called after successful creation or when user cancels.
   */
  onClose: () => void;
}

/**
 * A dialog component for creating new locations in the system.
 *
 * Features:
 * - Company association with automatic inventory status
 * - Location name validation with uniqueness check
 * - Optional address input with Google Places integration
 * - Inventory status toggle with smart defaults
 * - Form validation and error handling
 * - Automatic query cache invalidation on success
 *
 * Form Behavior:
 * - Company selection affects default inventory status
 * - Address is optional but must be complete if provided
 * - Shows error feedback for validation failures
 * - Handles unique constraint violations gracefully
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CreateLocationDialog
 *   isOpen={showDialog}
 *   onClose={() => setShowDialog(false)}
 * />
 *
 * // With default name
 * <CreateLocationDialog
 *   defaultName="New Warehouse"
 *   isOpen={showDialog}
 *   onClose={handleClose}
 * />
 * ```
 */
export function CreateLocationDialog(props: Props) {
  const queryClient = useQueryClient();

  // Setup mutation for creating new locations
  const createLocationMutation = useMutation({
    mutationFn: createLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location'] });
      close();
    },
    onError: (error) => {
      if (hasUniqueViolationError(error)) {
        setError('name', {
          type: 'custom',
          message: 'A location with this name already exists',
        });
      }
    },
  });

  // Initialize form with validation
  const { control, formState, reset, setValue, setError, handleSubmit } =
    useForm<InputFormData, unknown, OutputFormData>({
      resolver: zodResolver(outputSchema),
      defaultValues: {
        name: props.defaultName ?? '',
        company: null,
        address: null,
        isInventory: null,
      },
    });

  const companyController = useController({ control, name: 'company' });
  const inventoryController = useController({ control, name: 'isInventory' });

  const isInventorySelected = formState.dirtyFields.isInventory ?? false;
  const isAlertOpen =
    createLocationMutation.isError &&
    !hasUniqueViolationError(createLocationMutation.error) &&
    !isPlanLimitError(createLocationMutation.error);

  /**
   * Handles company selection changes.
   * Updates inventory status based on company type unless manually set.
   */
  const handleChangeCompany = (
    company: z.infer<typeof companySchema> | null
  ) => {
    companyController.field.onChange(company);

    // Don't override manual inventory selection
    if (isInventorySelected) {
      return;
    }

    // Reset inventory status when company is cleared
    if (company === null) {
      return setValue('isInventory', null);
    }

    // Set inventory status based on company type
    setValue('isInventory', !company.isCustomer);
  };

  /**
   * Handles dialog closure.
   * Resets form state and calls onClose callback.
   */
  const close = () => {
    props.onClose();
    reset();
  };

  /**
   * Handles creating a new customer.
   * Opens the customer creation page in a new tab with the provided name.
   */
  const handleCreateCustomer = (name: string) => {
    const url = getNewCompanyURL(name);

    window.open(url, '_blank');
  };

  /**
   * Handles form submission when validation passes.
   * Creates location with or without address details.
   */
  const handleValidSubmission: SubmitHandler<OutputFormData> = (data) => {
    // Use company ID as customerId if the company is not the manufacturer
    // Suppliers and customers are both stored in the customer table and can be assigned to locations
    // Only the manufacturer (isManufacturer: true) should result in customerId being null
    const customerId = data.company.isManufacturer ? null : data.company.id;

    createLocationMutation.mutate({
      customerId,
      name: data.name,
      type: 'SITE',
      isInventory: data.isInventory,
      address: data.address?.address ?? null,
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while creating the location."
        onClose={createLocationMutation.reset}
      />
      <DialogTitle>Add location</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box
            component="fieldset"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              m: 0,
            }}
          >
            <Typography
              component="legend"
              variant="subtitle2"
              sx={{
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <ArticleOutlined fontSize="small" color="action" />
              General
            </Typography>
            <Stack spacing={1.5}>
              <CompanySearch
                label="Company"
                name="company"
                value={companyController.field.value}
                error={Boolean(companyController.fieldState.error)}
                helperText={companyController.fieldState.error?.message}
                onChange={handleChangeCompany}
                onCreate={handleCreateCustomer}
              />
              <FormTextField name="name" label="Name" control={control} />
            </Stack>
          </Box>
          <FormAddressSearch name="address" label="Address" control={control} />
          <Box
            component="fieldset"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              m: 0,
            }}
          >
            <Typography
              component="legend"
              variant="subtitle2"
              sx={{
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <TuneOutlined fontSize="small" color="action" />
              Options
            </Typography>
            <LocationInventoryToggle
              isInventory={inventoryController.field.value}
              onChange={inventoryController.field.onChange}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
